<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import LzpCommandPicker from './LzpCommandPicker.vue';
import type { CommandEnumOption } from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * Command chooser shared by the bulk issue bar and the single-player inspector control:
 * the catalogue picker, or — for allowlisted operators — a free-text raw RUNCOMM# input
 * with a catalogue/raw toggle. Selection + raw text are owned by {@link useIssueCommandForm}
 * (passed via v-model); pressing Enter in the raw input emits `enter` so each host can run
 * its own guarded submit. `testIdPrefix` keeps data-testids unique across instances.
 */
defineProps<{
    commands: CommandEnumOption[];
    loading?: boolean;
    selected: CommandEnumOption | null;
    canIssueRaw?: boolean;
    testIdPrefix: string;
    /** Per-option picker gating for the host's targets (see useIssueCommandForm). Absent → no gating. */
    optionGateReason?: (command: CommandEnumOption) => string | null;
}>();

const rawMode = defineModel<boolean>('rawMode', { required: true });
const rawCommand = defineModel<string>('rawCommand', { required: true });

// Switching to raw focuses the input with the cursor after the prefilled RUNCOMM#,
// so the operator can type the rest straight away. Not on first render, so an
// inspector opening never steals focus.
const rawInput = ref<HTMLInputElement | null>(null);
watch(rawMode, async (isRaw) => {
    if (!isRaw) return;
    await nextTick();
    const input = rawInput.value;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
});

const emit = defineEmits<{
    (e: 'select', command: CommandEnumOption): void;
    (e: 'enter'): void;
}>();
</script>

<template>
    <!-- Catalogue chooser, or a free-text raw RUNCOMM# command for allowlisted operators. -->
    <input
        v-if="rawMode"
        ref="rawInput"
        v-model="rawCommand"
        type="text"
        spellcheck="false"
        autocomplete="off"
        placeholder="RUNCOMM#…"
        class="border-border/60 focus:border-primary text-foreground w-56 rounded-md border bg-transparent px-2 py-0.5 font-mono text-xs outline-none"
        :data-testid="`${testIdPrefix}-raw-command`"
        aria-label="Raw command"
        @keydown.enter.prevent="emit('enter')"
    />
    <LzpCommandPicker
        v-else
        :commands="commands"
        :loading="loading"
        :selected="selected"
        :option-gate-reason="optionGateReason"
        @select="emit('select', $event)"
    />
    <!-- Switch between catalogue and raw — only the trusted operators see this. -->
    <button
        v-if="canIssueRaw"
        type="button"
        class="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
        :data-testid="`${testIdPrefix}-raw-toggle`"
        @click="rawMode = !rawMode"
    >
        {{ rawMode ? 'catalogue' : 'raw' }}
    </button>
</template>
